import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, CircularProgress, Typography } from '@mui/joy';
import MicIcon from '@mui/icons-material/Mic';

import { crewAtcCommandDispatch } from '../../services/types/api.generated';
import { AgentStatus } from './types';

interface PushToCommandButtonProps {
  sessionId: string;
  droneUuids: string[];
  agentStatus: AgentStatus;
  setAgentStatus: (status: AgentStatus) => void;
  onSetMicrophone?: (enabled: boolean) => Promise<void>;
}

const PushToCommandButton: React.FC<PushToCommandButtonProps> = ({
  sessionId,
  droneUuids,
  agentStatus,
  setAgentStatus,
  onSetMicrophone
}) => {
  const { t } = useTranslation();

  const handleClick = useCallback(async () => {
    if (agentStatus === 'loading') return;

    if (agentStatus === 'idle') {
      // First click: start ATC command + unmute mic
      if (onSetMicrophone) {
        await onSetMicrophone(true);
      }

      setAgentStatus('loading');
      try {
        const response = await crewAtcCommandDispatch(sessionId, 'start', {
          device_uuids: droneUuids
        });
        if (response.status === 200) {
          setAgentStatus('active');
        } else {
          throw new Error(`ATC start failed with status ${response.status}`);
        }
      } catch (error) {
        console.error('ATC start command failed:', error);
        setAgentStatus('idle');
      }
    } else if (agentStatus === 'active') {
      try {
        await crewAtcCommandDispatch(sessionId, 'end', {
          device_uuids: droneUuids
        });
      } catch (error) {
        console.error('ATC end command failed:', error);
      }

      setAgentStatus('idle');
    }
  }, [sessionId, droneUuids, agentStatus, setAgentStatus, onSetMicrophone]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'v') {
        e.preventDefault();
        handleClick();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleClick]);

  const isIdle = agentStatus === 'idle';
  const isLoading = agentStatus === 'loading';
  const isActive = agentStatus === 'active';

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        width: '100%',
        minHeight: 40,
        px: 1.5,
        py: 1,
        borderRadius: 'sm',
        cursor: isLoading ? 'wait' : 'pointer',
        userSelect: 'none',
        transition: 'all 0.15s ease-in-out',
        border: 1,
        borderColor: isActive ? 'primary.bbYellow' : 'neutral.700',
        bgcolor: 'background.level1',
        ...(isActive && {
          background:
            'radial-gradient(circle, rgba(80,67,39,0.85) 0%, rgba(32,26,15,0.85) 100%)',
          borderColor: 'primary.bbYellow'
        }),
        '&:hover': {
          bgcolor: isActive ? undefined : 'neutral.800'
        }
      }}
    >
      {isLoading ? (
        <CircularProgress
          size='sm'
          sx={{ '--CircularProgress-size': '16px', color: 'text.primary' }}
        />
      ) : (
        <MicIcon
          sx={{
            fontSize: 16,
            transition: 'color 0.15s ease-in-out',
            ...(isActive
              ? {
                  background: 'linear-gradient(180deg, #ffcf40 0%, #ff9501 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }
              : {
                  color: 'text.primary'
                })
          }}
        />
      )}
      <Typography
        level='body-sm'
        sx={{
          fontWeight: isActive ? 600 : 400,
          fontSize: '0.75rem',
          lineHeight: '142%',
          color: 'common.offWhite',
          whiteSpace: 'nowrap',
          transition: 'color 0.15s ease-in-out'
        }}
      >
        {isLoading
          ? t('crew_view.connecting_agent', 'Connecting...')
          : isActive
            ? t('crew_view.execute_command', 'Execute')
            : t('crew_view.command', 'Command')}
      </Typography>
    </Box>
  );
};

export default PushToCommandButton;
